import React, { useState, useEffect, useMemo } from 'react';
import { doc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../auth/components/AuthProvider';
import * as SolicitudesProductoCampanaService from '../../repuestos/services/solicitudesProductoCampanaService';

function getSolicitudTitulo(s) {
  return s?.nombre || s?.nombre_producto || 'Sin nombre';
}

function getSolicitudImagenUrl(s) {
  if (Array.isArray(s?.imagenes) && s.imagenes.length > 0 && s.imagenes[0]) return s.imagenes[0];
  if (s?.imagenUrl) return s.imagenUrl;
  return null;
}

function comentarioEmpresaActual(s) {
  return s?.comentarioAdmin || s?.comentariosAdmin || '';
}

/** Ficha en vivo: datos guardados + borrador de edición. */
function mergeSolicitudProductoParaPreview(base, edit) {
  if (!base) return base;
  if (!edit) return base;
  const precio = edit.precio === '' ? base.precio : Number(edit.precio);
  const stock = edit.stock === '' ? base.stock : Number(edit.stock);
  return {
    ...base,
    nombre: edit.nombre,
    nombre_producto: edit.nombre,
    descripcion: edit.descripcion,
    precio: Number.isFinite(precio) ? precio : base.precio,
    categoria: edit.categoria,
    marca: edit.marca,
    stock: Number.isFinite(stock) ? Math.floor(stock) : base.stock,
    url: (edit.url || '').trim() || null,
  };
}

/** Vista previa tipo ficha pública (modal / panel admin). */
function SolicitudProductoFichaPreview({ s }) {
  const img = getSolicitudImagenUrl(s);
  const titulo = getSolicitudTitulo(s);
  const empresa =
    s?.empresaNombre || s?.nombre_empresa || s?.nombre_proveedor || s?.empresaId || '—';
  const precio = s?.precio != null ? Number(s.precio) : null;
  return (
    <div className="rounded-2xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white overflow-hidden shadow-inner mb-6">
      <div className="grid md:grid-cols-2 gap-0 md:gap-0">
        <div className="aspect-[4/3] md:aspect-auto md:min-h-[200px] bg-gray-100 relative">
          {img ? (
            <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">Sin imagen</div>
          )}
        </div>
        <div className="p-5 flex flex-col justify-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600 mb-1">Vista previa — ficha</p>
          <h3 className="text-xl font-bold text-gray-900 leading-tight">{titulo}</h3>
          <p className="text-sm text-gray-500 mt-1">{empresa}</p>
          {precio != null && !Number.isNaN(precio) && (
            <p className="text-2xl font-semibold text-gray-900 mt-3">
              ${precio.toLocaleString('es-CL')}
            </p>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            {s?.categoria && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{s.categoria}</span>
            )}
            {s?.marca && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{s.marca}</span>
            )}
            {s?.stock != null && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800">Stock: {s.stock}</span>
            )}
          </div>
          {s?.url && (
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-indigo-600 hover:underline mt-2 truncate"
            >
              Enlace externo del producto
            </a>
          )}
        </div>
      </div>
      {s?.descripcion && (
        <div className="px-5 pb-5 pt-0 border-t border-gray-100 bg-white">
          <p className="text-xs font-semibold text-gray-500 uppercase mt-4 mb-1">Descripción</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{s.descripcion}</p>
        </div>
      )}
    </div>
  );
}

function AdminSolicitudesProducto({ embedded = false, initialFilter = 'todos' }) {
  const { user } = useAuth();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter);
  const [detalleSolicitud, setDetalleSolicitud] = useState(null);
  const [solicitudEditForm, setSolicitudEditForm] = useState(null);
  const [comentarioAdmin, setComentarioAdmin] = useState('');
  const [rechazoMotivo, setRechazoMotivo] = useState('');
  const [accionando, setAccionando] = useState(false);

  useEffect(() => {
    if (!detalleSolicitud) {
      setSolicitudEditForm(null);
      return;
    }
    const s = detalleSolicitud;
    setSolicitudEditForm({
      nombre: s.nombre || s.nombre_producto || '',
      descripcion: s.descripcion || '',
      precio: s.precio != null && s.precio !== '' ? String(s.precio) : '',
      categoria: s.categoria || '',
      marca: s.marca || '',
      stock: s.stock != null && s.stock !== '' ? String(s.stock) : '',
      url: s.url || '',
    });
  }, [detalleSolicitud?.id]);

  useEffect(() => {
    cargarSolicitudes();
  }, [filter]);

  const cargarSolicitudes = async () => {
    try {
      setLoading(true);
      const data = await SolicitudesProductoCampanaService.fetchSolicitudesProductoAdmin(filter);
      setSolicitudes(data);
    } catch (error) {
      console.error('Error cargando solicitudes de productos:', error);
      setSolicitudes([]);
    } finally {
      setLoading(false);
    }
  };

  const puedeActivarRechazar = (s) =>
    s.estado === 'pendiente' || s.estado === 'en_revision';

  const previewMerged = useMemo(
    () =>
      detalleSolicitud
        ? mergeSolicitudProductoParaPreview(detalleSolicitud, solicitudEditForm)
        : null,
    [detalleSolicitud, solicitudEditForm]
  );

  const refrescarDetalleSolicitud = async (solicitudId) => {
    const snap = await getDoc(doc(db, 'solicitudes_producto', solicitudId));
    if (snap.exists()) {
      setDetalleSolicitud({ id: snap.id, ...snap.data() });
    }
  };

  const persistEdicionSolicitudProductoOrThrow = async () => {
    if (!detalleSolicitud?.id || !solicitudEditForm) return;
    if (!puedeActivarRechazar(detalleSolicitud)) return;
    const nombre = (solicitudEditForm.nombre || '').trim();
    if (!nombre) throw new Error('El nombre del producto es obligatorio.');
    const precio = solicitudEditForm.precio === '' ? 0 : Number(solicitudEditForm.precio);
    if (!Number.isFinite(precio) || precio < 0) {
      throw new Error('Indica un precio válido (número ≥ 0).');
    }
    const stockRaw =
      solicitudEditForm.stock === '' ? 0 : Number(solicitudEditForm.stock);
    if (!Number.isFinite(stockRaw) || stockRaw < 0) {
      throw new Error('El stock debe ser un número ≥ 0.');
    }
    const stock = Math.floor(stockRaw);
    await SolicitudesProductoCampanaService.actualizarCamposSolicitudProductoAdmin(
      detalleSolicitud.id,
      {
        nombre,
        descripcion: solicitudEditForm.descripcion,
        precio,
        categoria: solicitudEditForm.categoria,
        marca: solicitudEditForm.marca,
        stock,
        url: (solicitudEditForm.url || '').trim() || null,
      },
      user?.uid
    );
  };

  const handleGuardarEdicionSolicitud = async () => {
    if (!detalleSolicitud?.id || !solicitudEditForm) return;
    if (!puedeActivarRechazar(detalleSolicitud)) {
      alert('Solo se pueden editar solicitudes pendientes o en revisión.');
      return;
    }
    setAccionando(true);
    try {
      await persistEdicionSolicitudProductoOrThrow();
      await refrescarDetalleSolicitud(detalleSolicitud.id);
      await cargarSolicitudes();
      alert('Cambios de la solicitud guardados.');
    } catch (error) {
      console.error(error);
      alert(error?.message || 'No se pudieron guardar los cambios.');
    } finally {
      setAccionando(false);
    }
  };

  const handleAprobar = async () => {
    if (!detalleSolicitud?.id) return;
    setAccionando(true);
    try {
      await persistEdicionSolicitudProductoOrThrow();
      await SolicitudesProductoCampanaService.aprobarSolicitudProducto(
        detalleSolicitud.id,
        comentarioAdmin || null,
        user?.uid,
        detalleSolicitud
      );
      setDetalleSolicitud(null);
      setComentarioAdmin('');
      setRechazoMotivo('');
      await cargarSolicitudes();
      alert('Solicitud activada. El producto está publicado.');
    } catch (error) {
      console.error('Error aprobando solicitud:', error);
      const msg = error?.message || '';
      if (msg.includes('límite') || msg.includes('resource-exhausted')) {
        alert('No se pudo crear el producto: la empresa alcanzó el límite de productos de su plan.');
      } else {
        alert('Error al activar la solicitud: ' + msg);
      }
    } finally {
      setAccionando(false);
    }
  };

  const handleRechazar = async () => {
    if (!detalleSolicitud?.id) return;
    setAccionando(true);
    try {
      await persistEdicionSolicitudProductoOrThrow();
      await SolicitudesProductoCampanaService.rechazarSolicitudProducto(
        detalleSolicitud.id,
        rechazoMotivo || null,
        comentarioAdmin || null
      );
      setDetalleSolicitud(null);
      setComentarioAdmin('');
      setRechazoMotivo('');
      await cargarSolicitudes();
      alert('Solicitud rechazada.');
    } catch (error) {
      console.error('Error rechazando solicitud:', error);
      alert('Error al rechazar la solicitud');
    } finally {
      setAccionando(false);
    }
  };

  const eliminarSolicitud = async (solicitudId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta solicitud?')) return;
    try {
      await deleteDoc(doc(db, 'solicitudes_producto', solicitudId));
      if (detalleSolicitud?.id === solicitudId) setDetalleSolicitud(null);
      await cargarSolicitudes();
      alert('Solicitud eliminada exitosamente');
    } catch (error) {
      console.error('Error eliminando solicitud:', error);
      alert('Error al eliminar la solicitud');
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'pendiente': return 'bg-yellow-100 text-yellow-800';
      case 'en_revision': return 'bg-blue-100 text-blue-800';
      case 'aprobado':
      case 'activa': return 'bg-green-100 text-green-800';
      case 'rechazado':
      case 'rechazada': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoTexto = (estado) => {
    switch (estado) {
      case 'pendiente': return 'Pendiente';
      case 'en_revision': return 'En Revisión';
      case 'aprobado':
      case 'activa': return estado === 'activa' ? 'Activada' : 'Aprobado';
      case 'rechazado':
      case 'rechazada': return 'Rechazada';
      default: return estado || 'Sin estado';
    }
  };

  if (loading && solicitudes.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600">Cargando solicitudes...</span>
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-4' : 'space-y-6'}>
      {!embedded && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800 font-medium">
            📦 <strong>Solicitudes de productos</strong> — Revisa, activa o rechaza. Al activar se publica el producto y la empresa ve el enlace en su dashboard. Comentario interno y motivo de rechazo opcionales.
          </p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4 flex-wrap gap-2">
          <label className="text-sm font-medium text-gray-700">Filtrar por estado:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="todos">Todos</option>
            <option value="pendiente">Pendientes</option>
            <option value="en_revision">En Revisión</option>
            <option value="activa">Activadas</option>
            <option value="aprobado">Aprobados</option>
            <option value="rechazada">Rechazadas</option>
            <option value="rechazado">Rechazados</option>
          </select>
          <button
            onClick={cargarSolicitudes}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            🔄 Actualizar
          </button>
        </div>
      </div>

      {!embedded && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-yellow-600">
            {solicitudes.filter(s => s.estado === 'pendiente').length}
          </div>
          <div className="text-sm text-gray-600">Pendientes</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-blue-600">
            {solicitudes.filter(s => s.estado === 'en_revision').length}
          </div>
          <div className="text-sm text-gray-600">En Revisión</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-green-600">
            {solicitudes.filter(s => s.estado === 'aprobado' || s.estado === 'activa').length}
          </div>
          <div className="text-sm text-gray-600">Activadas / Aprobadas</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-red-600">
            {solicitudes.filter(s => s.estado === 'rechazado' || s.estado === 'rechazada').length}
          </div>
          <div className="text-sm text-gray-600">Rechazadas</div>
        </div>
      </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className={`font-medium text-gray-900 ${embedded ? 'text-base' : 'text-lg'}`}>
            Solicitudes de productos ({solicitudes.length})
          </h3>
          {embedded && (
            <p className="text-xs text-gray-500 mt-1">
              Puedes <strong>editar</strong> la solicitud en el detalle y <strong>guardar cambios</strong> antes de activar; al activar se usa lo guardado.
            </p>
          )}
        </div>
        {solicitudes.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No hay solicitudes de productos para mostrar.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {solicitudes.map((solicitud) => (
                  <tr key={solicitud.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {solicitud.nombre || solicitud.nombre_producto || 'Sin nombre'}
                        </div>
                        <div className="text-sm text-gray-500">{solicitud.categoria || 'Sin categoría'}</div>
                        {solicitud.precio != null && (
                          <div className="text-sm text-gray-500">${Number(solicitud.precio).toLocaleString()}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {solicitud.empresaNombre || solicitud.nombre_empresa || solicitud.nombre_proveedor || '—'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {solicitud.email_proveedor || solicitud.email_empresa || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEstadoColor(solicitud.estado)}`}>
                        {getEstadoTexto(solicitud.estado)}
                      </span>
                      {solicitud.accesoUrl && (
                        <a href={solicitud.accesoUrl} target="_blank" rel="noopener noreferrer" className="block text-xs text-blue-600 mt-1 truncate max-w-[180px]">
                          Ver publicado
                        </a>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {solicitud.fechaSolicitud?.toDate ? solicitud.fechaSolicitud.toDate().toLocaleDateString('es-ES') : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setDetalleSolicitud(solicitud);
                            setComentarioAdmin(comentarioEmpresaActual(solicitud));
                            setRechazoMotivo(solicitud.rechazoMotivo || '');
                          }}
                          className="text-blue-600 hover:underline text-xs font-medium"
                        >
                          Previsualizar ficha
                        </button>
                        {puedeActivarRechazar(solicitud) && (
                          <>
                            <button
                              onClick={() => {
                                setDetalleSolicitud(solicitud);
                                setComentarioAdmin('');
                                setRechazoMotivo('');
                              }}
                              className="text-green-600 hover:underline text-xs font-medium"
                            >
                              Activar
                            </button>
                            <button
                              onClick={() => {
                                setDetalleSolicitud(solicitud);
                                setRechazoMotivo('');
                                setComentarioAdmin(comentarioEmpresaActual(solicitud));
                              }}
                              className="text-red-600 hover:underline text-xs font-medium"
                            >
                              Rechazar
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => eliminarSolicitud(solicitud.id)}
                          className="text-gray-500 hover:text-gray-700 text-xs"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal detalle: payload, comentario, activar/rechazar */}
      {detalleSolicitud && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  Detalle: {detalleSolicitud.nombre || detalleSolicitud.nombre_producto || 'Solicitud'}
                </h2>
                <button
                  type="button"
                  onClick={() => { setDetalleSolicitud(null); setComentarioAdmin(''); setRechazoMotivo(''); setSolicitudEditForm(null); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {puedeActivarRechazar(detalleSolicitud) && solicitudEditForm && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 mb-6 space-y-3">
                  <p className="text-sm font-semibold text-amber-900">Editar solicitud</p>
                  <p className="text-xs text-amber-800/90">
                    Los cambios se guardan con el botón inferior. Al activar o rechazar también se aplican automáticamente si no guardaste antes.
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block sm:col-span-2 text-sm">
                      <span className="font-medium text-gray-700">Nombre</span>
                      <input
                        type="text"
                        value={solicitudEditForm.nombre}
                        onChange={(e) => setSolicitudEditForm((f) => ({ ...f, nombre: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block sm:col-span-2 text-sm">
                      <span className="font-medium text-gray-700">Descripción</span>
                      <textarea
                        value={solicitudEditForm.descripcion}
                        onChange={(e) => setSolicitudEditForm((f) => ({ ...f, descripcion: e.target.value }))}
                        rows={3}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="font-medium text-gray-700">Precio</span>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={solicitudEditForm.precio}
                        onChange={(e) => setSolicitudEditForm((f) => ({ ...f, precio: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="font-medium text-gray-700">Stock</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={solicitudEditForm.stock}
                        onChange={(e) => setSolicitudEditForm((f) => ({ ...f, stock: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="font-medium text-gray-700">Categoría</span>
                      <input
                        type="text"
                        value={solicitudEditForm.categoria}
                        onChange={(e) => setSolicitudEditForm((f) => ({ ...f, categoria: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="font-medium text-gray-700">Marca</span>
                      <input
                        type="text"
                        value={solicitudEditForm.marca}
                        onChange={(e) => setSolicitudEditForm((f) => ({ ...f, marca: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block sm:col-span-2 text-sm">
                      <span className="font-medium text-gray-700">URL externa (opcional)</span>
                      <input
                        type="text"
                        value={solicitudEditForm.url}
                        onChange={(e) => setSolicitudEditForm((f) => ({ ...f, url: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="https://…"
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={handleGuardarEdicionSolicitud}
                    disabled={accionando}
                    className="px-4 py-2 bg-amber-700 text-white text-sm font-medium rounded-lg hover:bg-amber-800 disabled:opacity-50"
                  >
                    {accionando ? 'Guardando…' : 'Guardar cambios de la solicitud'}
                  </button>
                </div>
              )}

              <SolicitudProductoFichaPreview s={previewMerged} />

              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Datos técnicos</p>
              <div className="space-y-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Empresa / ID:</span>{' '}
                  {detalleSolicitud.empresaNombre || detalleSolicitud.nombre_empresa || detalleSolicitud.empresaId}
                </div>
                <div>
                  <span className="font-medium text-gray-700">Nombre:</span>{' '}
                  {getSolicitudTitulo(previewMerged) || '—'}
                </div>
                {previewMerged?.descripcion ? (
                  <div>
                    <span className="font-medium text-gray-700">Descripción:</span>
                    <p className="mt-1 text-gray-600">{previewMerged.descripcion}</p>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-4">
                  {previewMerged?.precio != null && Number.isFinite(Number(previewMerged.precio)) && (
                    <span><strong>Precio:</strong> ${Number(previewMerged.precio).toLocaleString()}</span>
                  )}
                  {previewMerged?.categoria && <span><strong>Categoría:</strong> {previewMerged.categoria}</span>}
                  {previewMerged?.marca && <span><strong>Marca:</strong> {previewMerged.marca}</span>}
                  {previewMerged?.stock != null && <span><strong>Stock:</strong> {previewMerged.stock}</span>}
                </div>
                {Array.isArray(detalleSolicitud.imagenes) && detalleSolicitud.imagenes.length > 0 && (
                  <div>
                    <span className="font-medium text-gray-700">Imágenes:</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {detalleSolicitud.imagenes.slice(0, 5).map((url, i) => (
                        <img key={i} src={url} alt="" className="w-20 h-20 object-cover rounded border" />
                      ))}
                    </div>
                  </div>
                )}
                {detalleSolicitud.rechazoMotivo && (
                  <div className="bg-red-50 border border-red-200 rounded p-2">
                    <span className="font-medium text-red-800">Motivo rechazo:</span>
                    <p className="text-red-700">{detalleSolicitud.rechazoMotivo}</p>
                  </div>
                )}
                {detalleSolicitud.accesoUrl && (
                  <div>
                    <span className="font-medium text-gray-700">Enlace publicado:</span>
                    <a href={detalleSolicitud.accesoUrl} target="_blank" rel="noopener noreferrer" className="block text-blue-600 truncate">
                      {detalleSolicitud.accesoUrl}
                    </a>
                  </div>
                )}
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Comentario interno (opcional)</label>
                  <textarea
                    value={comentarioAdmin}
                    onChange={(e) => setComentarioAdmin(e.target.value)}
                    rows={2}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    placeholder="Comentario para uso interno o para la empresa"
                  />
                </div>
                {puedeActivarRechazar(detalleSolicitud) && (
                  <div>
                    <label className="block font-medium text-gray-700 mb-1">Motivo de rechazo (si rechaza)</label>
                    <textarea
                      value={rechazoMotivo}
                      onChange={(e) => setRechazoMotivo(e.target.value)}
                      rows={2}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      placeholder="Motivo visible para la empresa"
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t">
                {puedeActivarRechazar(detalleSolicitud) && (
                  <>
                    <button
                      type="button"
                      onClick={handleAprobar}
                      disabled={accionando}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {accionando ? 'Procesando...' : '✅ Activar y publicar'}
                    </button>
                    <button
                      type="button"
                      onClick={handleRechazar}
                      disabled={accionando}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {accionando ? '...' : '❌ Rechazar'}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => { setDetalleSolicitud(null); setComentarioAdmin(''); setRechazoMotivo(''); setSolicitudEditForm(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSolicitudesProducto;
